/*
  In-place image magnifier for the product image.

  cc: reworked from Dawn's original in three ways.

  1. It now activates on hover. Dawn's version, despite the setting being called
     "hover", required a click to start magnifying and then followed the pointer —
     so the feature looked broken until you happened to click.

  2. It magnifies from the largest candidate in the image's srcset rather than from
     `image.src`. Dawn used the on-screen file, so magnifying 2x on a ~1200px render
     just enlarged pixels it already had. The srcset now carries the full-resolution
     file (see snippets/product-thumbnail.liquid), which is what makes the detail on
     stones and etching actually resolve.

  3. The zoom ratio is merchant-set via `data-cc-zoom-ratio` rather than fixed at 2.

  The magnified layer is a background-image on an overlay sized to the image box, so
  the zoom happens inside the existing frame — the page does not open a larger view.
  Dawn's own CSS hides the lightbox trigger on hover-capable devices and restores it
  below 750px, so touch devices keep the tap-to-open gallery where hover cannot work.

  cc: three lifecycle rules exist because the pagination dots are overlaid on the
  bottom of the same image the magnifier owns (see assets/cc-product.css):

  a. A dead zone. The magnifier does not start — and tears down if it is already
     running — while the pointer is within DOT_BUFFER of the dots strip. Without it
     the image zooms under the customer while they are aiming at a 9px target, which
     moves what they are looking at at exactly the wrong moment.

  b. Cancellation. The full-resolution file is large and loads asynchronously, so the
     pointer can leave — or the customer can page to another image — long before it
     arrives. The original code inserted the overlay in `onload` regardless, which
     left that image permanently magnified: no mouseleave was ever coming to clear
     it, and `magnify()` then early-returned forever because an overlay existed.
     A pending load is now cancellable, and leaving the image cancels it.

  c. A reset hook. `window.ccResetMagnify` is called by assets/media-gallery.js on
     every media change, so a new image always starts unmagnified.
*/

/* Clearance around the dots strip, in px, within which the magnifier stays off. */
const DOT_BUFFER = 18;

/* The dots strip's box, or null when the gallery has no dots (single-image
   products) or they are not rendered at this breakpoint. */
function dotsRect(image) {
  const dots = image.closest('media-gallery')?.querySelector('.cc-gallery-dots');
  if (!dots || !dots.offsetParent) return null;
  const rect = dots.getBoundingClientRect();
  return rect.width && rect.height ? rect : null;
}

function inDotsZone(image, event) {
  const rect = dotsRect(image);
  if (!rect) return false;
  return (
    event.clientX >= rect.left - DOT_BUFFER &&
    event.clientX <= rect.right + DOT_BUFFER &&
    event.clientY >= rect.top - DOT_BUFFER &&
    event.clientY <= rect.bottom + DOT_BUFFER
  );
}

function createOverlay(image, sourceUrl, zoomRatio, lastPointer) {
  const pending = { cancelled: false };
  image.ccMagnifyPending = pending;

  const overlay = document.createElement('div');
  prepareOverlay(overlay, sourceUrl);

  image.style.opacity = '50%';
  setLoadingSpinner(image, true);

  /* Restores the image to its resting state whether the load succeeded, failed or
     was cancelled — the original toggled the spinner, which desynced the moment a
     load did not complete in the one order it expected. */
  const settle = () => {
    setLoadingSpinner(image, false);
    image.style.opacity = '100%';
    if (image.ccMagnifyPending === pending) image.ccMagnifyPending = null;
  };

  const overlayImage = new Image();
  overlayImage.onload = () => {
    settle();
    if (pending.cancelled) return;
    image.parentElement.insertBefore(overlay, image);
    /* The full-resolution file loads asynchronously, so the overlay does not exist
       yet when the pointer first enters. Without this the layer would appear at its
       natural size in the corner and only snap to the cursor on the next mousemove. */
    moveWithHover(image, lastPointer, zoomRatio);
  };
  overlayImage.onerror = settle;
  overlayImage.src = sourceUrl;

  return overlay;
}

function prepareOverlay(container, sourceUrl) {
  container.setAttribute('class', 'image-magnify-full-size');
  container.setAttribute('aria-hidden', 'true');
  container.style.backgroundImage = `url('${sourceUrl}')`;
  container.style.backgroundColor = 'var(--gradient-background)';
}

/* Idempotent, unlike a toggle: teardown can run at any point in the load and must
   not be able to leave the spinner stuck on. */
function setLoadingSpinner(image, visible) {
  const spinner = image.parentElement?.parentElement?.querySelector('.loading__spinner');
  if (spinner) spinner.classList.toggle('hidden', !visible);
}

/* Removes any magnification from this image and cancels one still loading. */
function teardown(image) {
  image.parentElement?.querySelector('.image-magnify-full-size')?.remove();
  if (image.ccMagnifyPending) {
    image.ccMagnifyPending.cancelled = true;
    image.ccMagnifyPending = null;
    setLoadingSpinner(image, false);
  }
  image.style.opacity = '100%';
}

function moveWithHover(image, event, zoomRatio) {
  const overlay = image.parentElement.querySelector('.image-magnify-full-size');
  if (!overlay) return;

  const ratio = image.height / image.width;
  const container = image.getBoundingClientRect();
  const xPosition = event.clientX - container.left;
  const yPosition = event.clientY - container.top;
  const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
  const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

  overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
  overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
}

/* The highest-resolution file the srcset offers. Falls back to the displayed src
   when a srcset is absent, which is Dawn's original behaviour. */
function highestResolutionSource(image) {
  if (!image.srcset) return image.src;
  const candidates = image.srcset
    .split(',')
    .map((entry) => {
      const [url, descriptor] = entry.trim().split(/\s+/);
      return { url, width: parseInt(descriptor, 10) || 0 };
    })
    .filter((c) => c.url);
  if (!candidates.length) return image.src;
  return candidates.reduce((best, c) => (c.width > best.width ? c : best)).url;
}

function magnify(image, zoomRatio, pointer) {
  if (image.parentElement.querySelector('.image-magnify-full-size')) return;
  if (image.ccMagnifyPending) return;
  const overlay = createOverlay(image, highestResolutionSource(image), zoomRatio, pointer);
  overlay.onmousemove = (event) => {
    if (inDotsZone(image, event)) {
      teardown(image);
      return;
    }
    moveWithHover(image, event, zoomRatio);
  };
  overlay.onmouseleave = () => teardown(image);
  /* Clicking the magnified layer dismisses it, so there is always a way out
     without having to leave the image. */
  overlay.onclick = () => teardown(image);
}

function enableZoomOnHover(defaultRatio) {
  document.querySelectorAll('.image-magnify-hover').forEach((image) => {
    if (image.dataset.ccMagnifyBound) return;
    image.dataset.ccMagnifyBound = 'true';

    const host = image.closest('[data-cc-zoom-ratio]');
    const zoomRatio = parseFloat(host?.dataset.ccZoomRatio) || defaultRatio;

    image.addEventListener('mouseenter', (event) => {
      if (inDotsZone(image, event)) return;
      magnify(image, zoomRatio, event);
      moveWithHover(image, event, zoomRatio);
    });

    /* Entering from an edge can land the pointer before the layer is ready; tracking
       movement on the image as well as the overlay keeps the two in step. Once the
       overlay exists it covers the image and receives the movement instead, so this
       also restarts the magnifier after a teardown near the dots — moving back up
       into the image resumes it without having to leave and re-enter. */
    image.addEventListener('mousemove', (event) => {
      if (inDotsZone(image, event)) {
        teardown(image);
        return;
      }
      if (!image.parentElement.querySelector('.image-magnify-full-size')) {
        magnify(image, zoomRatio, event);
        return;
      }
      moveWithHover(image, event, zoomRatio);
    });

    /* Cancels a load still in flight. Without this, an overlay requested on a brief
       pass over the image lands after the pointer has gone and sticks.

       This is bound to the media container, NOT the image. The overlay is a
       *sibling* of the image that covers it, not a descendant, so the moment it is
       inserted the pointer leaves the image — as it also does crossing Dawn's zoom
       icon or the lightbox button. Tearing down on an image-level mouseleave would
       therefore remove the overlay, drop the pointer back on the image, magnify
       again, and loop. mouseleave on the container fires only when the pointer
       leaves the whole media box, which is the actual "gone" signal, and it catches
       exits made from any child rather than just from the image. */
    image.closest('.product-media-container')?.addEventListener('mouseleave', () => teardown(image));
  });
}

/* Called by assets/media-gallery.js whenever the active media changes, so paging
   with the dots or the thumbnail rail always lands on an unmagnified image. */
window.ccResetMagnify = (root) => {
  (root || document).querySelectorAll('.image-magnify-hover').forEach(teardown);
};

enableZoomOnHover(2.5);

/* The section re-renders on variant change, replacing these images. */
document.addEventListener('shopify:section:load', () => enableZoomOnHover(2.5));
